import { differenceInSeconds } from '@repo/date-time';
import { AuthenticationError } from '@repo/domain-service';
import { IssueAccessTokenDocument } from '../graphql/generated/graphql';
import { parseGraphQlError } from '../utils/parseGraphQlError';
import { readHeldRolesFromAccessToken } from './readHeldRolesFromAccessToken';
/**
 * Short-lived signed access token (JWT) held alongside the long-lived session
 * token. The backend accepts both: it tries the access token first and falls
 * back to the session token, so a missing or stale access token never fails a
 * request — it only costs the JWT fast-path.
 *
 * Deliberately in-memory: the token lives ~5 minutes, so persisting it buys
 * nothing but a second bearer credential in storage. After a page reload the
 * first request goes out on the session token alone and a single
 * `issueAccessToken` call primes the next one.
 *
 * The manager is a module singleton because the token is a property of the
 * session, not of any one repository — every repository shares one client and
 * therefore one token.
 */
/**
 * Values the backend returns in the `X-Access-Token-Status` response header.
 * The header is present *only* when the access token was rejected; its absence
 * means the token was accepted.
 */
const AccessTokenStatus = {
    Expired: 'expired',
    Invalid: 'invalid',
};
/**
 * Renew this many seconds before the stated expiry, so a token that would die
 * mid-flight is replaced rather than sent. The backend issues 5-minute tokens.
 */
const RENEWAL_MARGIN_SECONDS = 30;
const ACCESS_TOKEN_REJECTED_MESSAGE = 'Access token rejected.';
let repositoryConfig;
let accessToken;
let expiresAt;
/** Session token the current access token was issued against. */
let issuedForSessionToken;
let isRenewing = false;
/** The renewal in flight, so a caller that cannot go out without a token can wait for it. */
let renewalPromise;
let authenticationFailurePending = false;
/** Serialized form of the roles last reported, so the same roles are never reported twice. */
let reportedHeldRolesSignature;
const clear = () => {
    accessToken = undefined;
    expiresAt = undefined;
    issuedForSessionToken = undefined;
};
/**
 * Tell the host app which roles the signed-in person holds — `undefined` while
 * they are not known. Only a change is reported, because `getAccessToken` runs
 * before every request and would otherwise repeat the same answer each time.
 */
const reportHeldRoles = (heldRoles) => {
    const signature = JSON.stringify(heldRoles ?? null);
    if (signature === reportedHeldRolesSignature)
        return;
    reportedHeldRolesSignature = signature;
    repositoryConfig?.onHeldRolesChange?.(heldRoles);
};
/**
 * Drop the access token together with the roles read from it. Used when the
 * session itself is gone or has changed — unlike an expiry, where the roles
 * stay on screen until the renewal replaces them.
 */
const forgetSession = () => {
    clear();
    reportHeldRoles(undefined);
};
/**
 * Drop the access token and mark the session as failed, so the next unwrapped
 * response surfaces an `AuthenticationError` and the global auth observer runs
 * its usual logout path.
 */
const failAuthentication = () => {
    forgetSession();
    authenticationFailurePending = true;
};
const isFresh = () => {
    if (!expiresAt)
        return false;
    return differenceInSeconds(new Date(expiresAt), new Date()) > RENEWAL_MARGIN_SECONDS;
};
const performRenewal = async () => {
    const client = repositoryConfig?.client;
    if (!client)
        return;
    const result = await client.mutation(IssueAccessTokenDocument, {}).toPromise();
    if (result.error) {
        const error = parseGraphQlError(result.error, 'Failed to issue access token');
        // Only a rejected session token means the session is actually gone. A
        // transport failure, a rate limit or a backend fault says nothing about it,
        // so those leave the user logged in — the request that triggered this
        // renewal succeeded on the session token anyway, and the next one retries.
        if (error instanceof AuthenticationError) {
            failAuthentication();
        }
        return;
    }
    const issued = result.data?.issueAccessToken;
    if (!issued)
        return;
    // Re-read the session token rather than closing over it: the renewal is
    // fire-and-forget, so the session may have changed while it was in flight.
    setAccessToken(issued.token, issued.expiresAt, repositoryConfig?.getToken());
};
/**
 * Renewal is fire-and-forget. Blocking the request that noticed the stale token
 * would add latency to a flow that succeeds anyway on the session token, so the
 * request goes out now and the fresh token applies from the next one.
 *
 * `isRenewing` is set before the first client call so that the renewal mutation
 * itself — which passes back through `getAccessToken` — cannot start a second
 * renewal. It also keeps concurrent requests to one call, well inside the
 * backend's 30-per-minute budget.
 */
const renew = () => {
    if (isRenewing)
        return;
    // `issueAccessToken` authenticates with the session token, so there is
    // nothing to renew against without one.
    if (!repositoryConfig?.getToken())
        return;
    isRenewing = true;
    renewalPromise = performRenewal().finally(() => {
        isRenewing = false;
        renewalPromise = undefined;
    });
};
/**
 * Wire the manager to the repository config. Called by `ParkAppRepository`,
 * which owns the client the renewal mutation needs.
 */
export const initAccessTokenManager = (config) => {
    repositoryConfig = config;
    reportedHeldRolesSignature = undefined;
    forgetSession();
    authenticationFailurePending = false;
};
/**
 * Store the access token returned alongside a session token by a login mutation
 * or a renewal, and report the roles its `roles` claim carries.
 */
export const setAccessToken = (token, expiresAtValue, sessionToken) => {
    accessToken = token;
    expiresAt = expiresAtValue;
    issuedForSessionToken = sessionToken;
    reportHeldRoles(readHeldRolesFromAccessToken(token));
};
/**
 * The token to send as `X-Access-Token`, or `undefined` when there is nothing
 * fresh to send — in which case the request proceeds on the session token alone
 * and a renewal is started for the next one.
 */
export const getAccessToken = () => {
    const sessionToken = repositoryConfig?.getToken();
    // No session at all (guest before registration, or after logout) — there is
    // nothing to issue a token against.
    if (!sessionToken) {
        forgetSession();
        return undefined;
    }
    // The session changed under us (logout then login, or impersonation), so a
    // token issued against the previous one is meaningless, and so are its roles.
    if (issuedForSessionToken !== undefined && issuedForSessionToken !== sessionToken) {
        forgetSession();
    }
    if (accessToken && isFresh())
        return accessToken;
    renew();
    return undefined;
};
/**
 * Like `getAccessToken`, but when there is no fresh token it waits for the
 * renewal instead of going on without one — for requests authorized by the
 * access token alone. `undefined` when there is no session or the renewal failed.
 */
export const ensureAccessToken = async () => {
    const token = getAccessToken();
    if (token)
        return token;
    await renewalPromise;
    return accessToken && isFresh() ? accessToken : undefined;
};
/**
 * React to the `X-Access-Token-Status` response header.
 *
 * An unrecognised status is ignored rather than treated as a rejection: the
 * request it arrived on succeeded on the session token, and logging the user
 * out over a status string we do not know yet would turn a backend contract
 * addition into a session loss.
 */
export const handleAccessTokenStatus = (status) => {
    if (status === AccessTokenStatus.Expired) {
        clear();
        renew();
        return;
    }
    if (status === AccessTokenStatus.Invalid) {
        failAuthentication();
    }
};
/**
 * Take the pending authentication failure, if any. Consuming it here is what
 * turns a rejected access token — which arrives on an otherwise *successful*
 * response — into the same `AuthenticationError` an `"Unauthenticated."`
 * response produces.
 */
export const takeAuthenticationFailure = () => {
    if (!authenticationFailurePending)
        return undefined;
    authenticationFailurePending = false;
    return new AuthenticationError(ACCESS_TOKEN_REJECTED_MESSAGE);
};
